import { UseFormReturn, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { CCTALK_INTERFACE_TRANSLATION_MAP, MANUFACTURER_TRANSLATION_MAP } from 'src/features/systems/utils/constants';

import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { SystemComponentFormSchemaValues } from 'src/features/systems/utils/formSchema';
import {
    BANKNOTE_VALIDATOR_MANUFACTURER,
    CCTALK_INTERFACE_MODAL,
    COIN_HOPPER_MANUFACTURER,
    COIN_MECHANISM_ACCEPTOR_MANUFACTURER,
    SYSTEM_COMPONENT,
} from 'src/features/systems/utils/types';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { useListPayStations } from 'src/features/payment/api/useListPayStations';
import { useParking } from 'src/providers/ParkingProvider';

type BanknoteCoinComponentsFormFieldsProps = {
    form: UseFormReturn<SystemComponentFormSchemaValues>;
};

const BanknoteCoinComponentsFormFields = ({ form }: BanknoteCoinComponentsFormFieldsProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const component = useWatch({ control: form.control, name: 'component' });

    const { data: payStationsData } = useListPayStations({ parkingId });

    return (
        <>
            <FormField
                control={form.control}
                name="pay_station"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(paymentsMessages.payStation)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(paymentsMessages.payStation)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {payStationsData?.results?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {option.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <SelectFormField<SystemComponentFormSchemaValues>
                options={Object.values(
                    component === SYSTEM_COMPONENT.BANKNOTE_VALIDATOR
                        ? BANKNOTE_VALIDATOR_MANUFACTURER
                        : component === SYSTEM_COMPONENT.COIN_HOPPER
                          ? COIN_HOPPER_MANUFACTURER
                          : COIN_MECHANISM_ACCEPTOR_MANUFACTURER,
                )}
                form={form}
                name="data.manufacturer"
                label={formatMessage(systemsMessages.manufacturer)}
                optionTranslationMap={MANUFACTURER_TRANSLATION_MAP}
            />

            {component !== SYSTEM_COMPONENT.CCTALK_INTERFACE ? (
                <InputFormField<SystemComponentFormSchemaValues>
                    form={form}
                    name="data.model"
                    label={formatMessage(systemsMessages.model)}
                />
            ) : (
                <SelectFormField<SystemComponentFormSchemaValues>
                    options={Object.values(CCTALK_INTERFACE_MODAL)}
                    form={form}
                    name="data.model"
                    label={formatMessage(systemsMessages.model)}
                    optionTranslationMap={CCTALK_INTERFACE_TRANSLATION_MAP}
                />
            )}
            <InputFormField<SystemComponentFormSchemaValues>
                form={form}
                name="data.serial_number"
                label={formatMessage(systemsMessages.serialNumber)}
            />
            <InputFormField<SystemComponentFormSchemaValues>
                form={form}
                name="data.firmware_version"
                label={formatMessage(systemsMessages.firmwareVersion)}
            />
            {component !== SYSTEM_COMPONENT.CCTALK_INTERFACE && (
                <InputFormField<SystemComponentFormSchemaValues>
                    form={form}
                    name="data.currency_dataset"
                    label={formatMessage(systemsMessages.currencyDataset)}
                />
            )}
        </>
    );
};

export default BanknoteCoinComponentsFormFields;
